import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LODGING_ACCENT_COLOR } from '../../utils/tourLodgingUi';
import { StopParticipantsRow } from './StopParticipantsRow';

/** Join / lodging chips + compact "who's going" avatars for a tour stop. */
export function TourStopSocialFooter({
  theme,
  isDarkMode,
  isLodging = false,
  isJoined = false,
  canEditLodging = false,
  onToggleLodging,
  onToggleJoin,
  participantIds = [],
  members = [],
  compact = false,
}) {
  const styles = useMemo(() => createStyles(theme, isDarkMode, compact), [theme, isDarkMode, compact]);
  const showParticipants = participantIds.length > 0;
  const showActions = (canEditLodging && onToggleLodging) || onToggleJoin;

  if (!showActions && !showParticipants) return null;

  return (
    <View style={styles.wrap}>
      {showActions ? (
        <View style={styles.actions}>
          {canEditLodging && onToggleLodging ? (
            <TouchableOpacity
              style={[styles.chipBtn, isLodging && styles.chipBtnLodging]}
              onPress={onToggleLodging}
              accessibilityLabel={isLodging ? 'Clear lodging for this stop' : 'Mark as tonight lodging'}
            >
              <Ionicons
                name="bed-outline"
                size={compact ? 12 : 13}
                color={isLodging ? LODGING_ACCENT_COLOR : theme.text.secondary}
              />
              <Text style={[styles.chipBtnText, isLodging && styles.chipBtnTextLodging]}>
                {isLodging ? 'Lodging' : 'Set lodging'}
              </Text>
            </TouchableOpacity>
          ) : null}
          {onToggleJoin ? (
            <TouchableOpacity
              style={[styles.chipBtn, isJoined && styles.chipBtnJoined]}
              onPress={onToggleJoin}
              accessibilityLabel={isJoined ? 'Remove yourself from this stop' : 'Join this stop'}
            >
              <Ionicons
                name={isJoined ? 'checkmark-circle' : 'person-add-outline'}
                size={compact ? 12 : 13}
                color={isJoined ? theme.brand.accent : theme.text.secondary}
              />
              <Text style={[styles.chipBtnText, isJoined && styles.chipBtnTextJoined]}>
                {isJoined ? 'Going' : 'Join'}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
      {showParticipants ? (
        <StopParticipantsRow
          participantIds={participantIds}
          members={members}
          theme={theme}
          size={compact ? 20 : 22}
          style={showActions ? styles.participants : undefined}
        />
      ) : null}
    </View>
  );
}

function createStyles(theme, isDarkMode, compact) {
  return StyleSheet.create({
    wrap: { gap: compact ? 6 : 8 },
    actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    participants: { marginTop: 2 },
    chipBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: compact ? 7 : 8,
      paddingVertical: compact ? 4 : 5,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    chipBtnLodging: {
      borderColor: LODGING_ACCENT_COLOR,
      backgroundColor: isDarkMode ? 'rgba(124,108,221,0.2)' : 'rgba(124,108,221,0.1)',
    },
    chipBtnJoined: {
      borderColor: theme.brand.accent,
      backgroundColor: `${theme.brand.accent}18`,
    },
    chipBtnText: { fontSize: compact ? 10 : 11, fontWeight: '700', color: theme.text.secondary },
    chipBtnTextLodging: { color: LODGING_ACCENT_COLOR },
    chipBtnTextJoined: { color: theme.brand.accent },
  });
}

export default TourStopSocialFooter;
